import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    makeStyles,
  } from "@material-ui/core";
  import * as React from "react";
  import { Fragment, useState } from "react";
  import {
    Button,
    useRefresh,
    useUnselectAll,
    useDataProvider,
  } from "react-admin";
  import SaveIcon from "@material-ui/icons/Save";
  import { delay } from "utils/tools";
  import TableResultConfirmCommercialDocuments from "./TableResultConfirmCommercialDocuments";
  import { findIndex } from "lodash";
  
  interface IBulkActionProps {
    filteredData: any[];
    documentType: string;
  }
  
  interface IResults {
    id: number;
    mbcRef?: string;
    description: string;
    type: string;
    status: string;
    processingStatus: string;
  }
  
  const useStyles = makeStyles({
    actionColor: {
      color: "#FFF",
    },
    dialog: {
      "& .MuiDialog-paper": {
        overflowY: "unset",
      },
    },
  });
  
  const BulkActionDeleteButton = ({
    filteredData,
    documentType,
  }: IBulkActionProps) => {
    const [open, setOpen] = useState(false);
    const [finished, setFinished] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [results, setResults] = useState<IResults[]>([]);
  
    const refresh = useRefresh();
    const unselectAll = useUnselectAll();
    const provider = useDataProvider();
    const classes = useStyles();
  
    const selectedCommercialDocuments = filteredData.map((data) => ({
      id: data.id,
      mbcRef: data.mbcRef,
      description: data.items[0].description,
      type: data.invoiceTaggedMbcRef ? "credit_note_tagged" : data.type,
      status: data.status,
      processingStatus: "Not Processed",
    }));
  
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
      if (finished) {
        refresh();
      }
  
      setOpen(false);
    };
  
    const deleteDraftCommercialDocument = (
      selectedId: number,
      type: string,
      index: number,
      retryCount: number = 1
    ) => {
      return new Promise((resolve, reject) => {
        provider
          .customDelete(`commercialDocuments/${selectedId}`, {
            data: {},
          })
          .then(() => {
            let tempInvoices = selectedCommercialDocuments;
            tempInvoices[index].status = "deleted";
            tempInvoices[index].processingStatus = "Finished";
  
            setResults([...tempInvoices]);
            resolve(true);
          })
          .catch(({ message }: any) => {
            retryCount++;
  
            if (retryCount <= 3) {
              // give random delay between 2 - 3 seconds
              const delay = Math.floor(Math.random() * 1000) + 2000;
              setTimeout(() => {
                deleteDraftCommercialDocument(selectedId, type, index, retryCount)
                  .then(resolve)
                  .catch(reject);
              }, delay);
  
              return;
            }
  
            let tempInvoices = selectedCommercialDocuments;
            tempInvoices[index].processingStatus = `Error, ${message}`;
  
            setResults([...tempInvoices]);
            reject();
          });
      });
    };

    const deleteUnpostedCommercialDocument = (
      mbcRef: string,
      type: string,
      index: number,
      retryCount: number = 1
    ) => {
      return new Promise((resolve, reject) => {
        provider
          .customDelete(`commercialDocuments/mbc/${mbcRef}`, {
            data: {},
          })
          .then(() => {
            let tempInvoices = selectedCommercialDocuments;
            tempInvoices[index].status = "deleted";
            tempInvoices[index].processingStatus = "Finished";
  
            setResults([...tempInvoices]);
            resolve(true);
          })
          .catch(({ message }: any) => {
            retryCount++;
  
            if (retryCount <= 3) {
              // give random delay between 2 - 3 seconds
              const delay = Math.floor(Math.random() * 1000) + 2000;
              setTimeout(() => {
                deleteUnpostedCommercialDocument(mbcRef, type, index, retryCount)
                  .then(resolve)
                  .catch(reject);
              }, delay);
  
              return;
            }
  
            let tempInvoices = selectedCommercialDocuments;
            tempInvoices[index].processingStatus = `Error, ${message}`;
  
            setResults([...tempInvoices]);
            reject();
          });
      });
    };
  
    const handleConfirm = async (selectedCommercialDocuments: IResults[]) => {
      await setProcessing(true);
  
      for (const selectedCommercialDocument of selectedCommercialDocuments) {
        selectedCommercialDocument.processingStatus = "Loading...";
        setResults([...selectedCommercialDocuments]);
  
        const index = findIndex(selectedCommercialDocuments, function (o) {
          return o.id === selectedCommercialDocument.id;
        });
  
        try {
          switch (selectedCommercialDocument.status) {
            case "unposted":
                // Delete commercial document with status = Unposted
                await deleteUnpostedCommercialDocument(
                  selectedCommercialDocument.mbcRef,
                  selectedCommercialDocument.type,
                  index
                );

              break;
            case "draft":
                // Delete commercial document with status = Draft
                await deleteDraftCommercialDocument(
                  selectedCommercialDocument.id,
                  selectedCommercialDocument.type,
                  index
                );
  
              break;
          }
        } catch (error) {
          continue;
        }
  
        await delay(2000);
      }
  
      await setFinished(true);
      await setProcessing(false);
      await unselectAll("commercialDocuments");
    };
  
    return (
      <Fragment>
        <Button label="Delete Selected" onClick={handleOpen} style={{ backgroundColor: "#DA4040" }} />
        <Dialog
          open={open}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className={classes.dialog}
          maxWidth="lg"
        >
          <DialogTitle id="alert-dialog-title">
            Delete Selected {documentType}s
          </DialogTitle>
          <DialogContent>
            {results.length > 0 ? (
              <TableResultConfirmCommercialDocuments records={results} />
            ) : (
              <DialogContentText color="initial">
                Are you sure you want to delete this selected{" "}
                {documentType.toLowerCase()}s?
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions>
            {!finished && (
              <Button
                onClick={() => handleConfirm(selectedCommercialDocuments)}
                color="primary"
                label="Confirm"
                disabled={processing}
              >
                <SaveIcon />
              </Button>
            )}
            <Button
              onClick={handleClose}
              color="secondary"
              label={`${finished ? "Finish" : "Back"}`}
              disabled={processing}
            />
          </DialogActions>
        </Dialog>
      </Fragment>
    );
  };
  
  export default BulkActionDeleteButton;
  